import { mobileApiRequest, sheetServerRequest } from '../../apiRequests'
import { getSummaryAction } from './summaryUtils'
import { combineFilters, packFilter } from '../../../components/Insights/Filter'
import dayjs from 'dayjs'

/**
 * Fetches a saved summary configuration by key.
 */
export const getSummaryConfig = async (summaryKey) => {
    const action = {
        action: 'getObject',
        class: 'scoop.insight.Insight',
        key: summaryKey,
    }
    return await mobileApiRequest(action)
}

/**
 * Legacy function - fetches summary data using a single config object.
 *
 * @deprecated Use getSummaryDataFromSplitConfig instead
 */
export const getSummaryData = async (summaryConfig, prompts) => {
    const action = getSummaryAction(summaryConfig, prompts)
    return await mobileApiRequest(action)
}

/**
 * Fetches summary data from the appropriate endpoint using split configs.
 *
 * @param {Object} dataConfig - Data configuration (selectedItems, categoryAxis, etc.)
 * @param {Object} queryConfig - Query configuration (period, timeRange, etc.)
 * @param {Object} viewConfig - View configuration (view type affects API action)
 * @param {Array} prompts - Active prompts for filtering
 * @returns {Promise<Object>} - API response with summary data
 */
export const getSummaryDataFromSplitConfig = async (
    dataConfig,
    queryConfig,
    viewConfig,
    prompts
) => {
    const action = buildSummaryAction(dataConfig, queryConfig, viewConfig, prompts)

    // Determine which endpoint to use based on whether items are from worksheets
    const isWorksheet = dataConfig?.selectedItems?.some((m) => m.worksheetID)

    return isWorksheet ? await sheetServerRequest(action) : await mobileApiRequest(action)
}

/**
 * Builds the API action object from split configs.
 *
 * @param {Object} dataConfig - Data configuration
 * @param {Object} queryConfig - Query configuration
 * @param {Object} viewConfig - View configuration (view type affects action)
 * @param {Array} prompts - Active prompts
 * @returns {Object} - Action object for API request
 */
const buildSummaryAction = (dataConfig, queryConfig, viewConfig, prompts) => {
    const action = {
        metrics: dataConfig?.selectedItems || [],
        period: queryConfig?.period || 'Monthly',
    }

    // Extract relevant config values
    const view = viewConfig?.view || 'chart'
    const categoryAxis = dataConfig?.categoryAxis || 'Time'
    const disableTimeSeries = queryConfig?.disableTimeSeries || false
    const tableTimeSeries = queryConfig?.tableTimeSeries !== false
    const calendarType = queryConfig?.calendarType || 'Rolling'
    const sortColumns = queryConfig?.sortColumns

    // Determine action type based on view and settings
    if (view === 'chart') {
        if (categoryAxis === 'Time' && !disableTimeSeries) {
            action.action = 'getTimeSeries'
            action.split = true
            action.process = false
            action.calendarType = calendarType
        } else {
            action.action = 'getDataset'
            action.categoryAxis = categoryAxis
            if (sortColumns?.some((col) => col.column)) {
                action.sortColumns = sortColumns
            }
        }
    } else if (view === 'kpi') {
        if (tableTimeSeries) {
            action.action = 'getTimeSeries'
            action.calendarType = calendarType
            action.split = true
            action.process = false
        } else {
            action.action = 'getDataset'
        }
    } else if (view === 'table') {
        // Table view - uses dataset with specific handling
        if (categoryAxis === 'Time' && tableTimeSeries) {
            action.action = 'getTimeSeries'
            action.calendarType = calendarType
            action.split = false // Tables don't split by series
            action.process = false
        } else {
            action.action = 'getDataset'
            action.categoryAxis = categoryAxis
            if (sortColumns?.some((col) => col.column)) {
                action.sortColumns = sortColumns
            }
        }
    }

    // Time range
    if (queryConfig?.timeRange) {
        action.timeRange = queryConfig.timeRange

        if (queryConfig.timeRange === 'Custom') {
            if (queryConfig.from) {
                action.start = dayjs(queryConfig.from).format('MM/DD/YYYY')
            }
            if (queryConfig.to) {
                action.end = dayjs(queryConfig.to).format('MM/DD/YYYY')
            }
        }
    }

    // Prompts
    if (prompts?.length > 0) {
        const validPrompts = prompts
            .flat()
            .filter((p) => p?.filterValue?.values?.length > 0 && p.filterValue.values[0] !== 'All')

        if (validPrompts.length > 0) {
            action.prompts = packFilter(validPrompts)
        }
    }

    // Drill attribute
    if (dataConfig?.drillAttribute) {
        action.drillAttribute = dataConfig.drillAttribute
    }

    // Display rows (limit)
    if (queryConfig?.displayRows && queryConfig.displayRows !== 'All') {
        action.limit = queryConfig.displayRows
    }

    // Filters
    const filter = dataConfig?.filter
    const savedFilter = dataConfig?.savedFilter

    if (filter || savedFilter) {
        action.filter = savedFilter ? combineFilters(filter, savedFilter.filter) : filter
    }

    return action
}
